import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@mlm/ui';
import { listCourseAnnouncements } from '../lib/api';
import { ReportDialog } from './report-dialog';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Announcements from the instructor for THIS course, newest first.
 *
 * `highlightId` comes from the `?announcement=` deep link a bell notification
 * carries, so tapping the bell lands on the exact post rather than the top of
 * a list. Failure renders as an error with a retry, never as an empty state:
 * telling an enrolled learner "no announcements" because their connection
 * dropped is the same class of lie the certificate verify page used to tell.
 */
export function CourseAnnouncements({
  courseId,
  highlightId,
}: {
  courseId: string;
  highlightId?: string | null;
}) {
  const q = useQuery({
    queryKey: ['course-announcements', courseId],
    queryFn: () => listCourseAnnouncements(courseId),
  });

  if (q.isLoading) return <p className="py-4 text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        Could not load announcements.{' '}
        <button type="button" className="text-primary hover:underline" onClick={() => q.refetch()}>
          Try again
        </button>
      </p>
    );
  }
  const items = q.data ?? [];
  if (items.length === 0) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        No announcements from your instructor yet.
      </p>
    );
  }

  return (
    <div className="space-y-3 py-2">
      {items.map((a) => (
        <Card
          key={a.id}
          className={a.id === highlightId ? 'border-primary ring-1 ring-primary' : undefined}
        >
          <CardContent className="space-y-1 py-4">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="font-semibold">{a.title}</h3>
              <span className="flex-none text-xs text-muted-foreground">
                {formatDate(a.publishedAt)}
              </span>
            </div>
            <div
              className="prose prose-sm max-w-none text-sm text-muted-foreground"
              // Sanitized server-side by the same pipeline as article lessons.
              dangerouslySetInnerHTML={{ __html: a.body }}
            />
            <div className="flex justify-end pt-1">
              <ReportDialog targetKind="announcement" targetId={a.id} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
